export const extractScheduleArray = (scheduleData) => {
  const normalizeScheduleEntries = (scheduleArray) => {
    if (!Array.isArray(scheduleArray)) {
      return [];
    }

    return scheduleArray.map((item) => {
      if (!item || typeof item !== 'object' || !item.date) {
        return item;
      }

      return {
        ...item,
        openingTime: item.openingTime || (item.open ? `${item.date}T${item.open}` : null),
        closingTime: item.closingTime || (item.close ? `${item.date}T${item.close}` : null)
      };
    });
  };

  if (Array.isArray(scheduleData)) {
    return normalizeScheduleEntries(scheduleData);
  }

  if (Array.isArray(scheduleData?.schedule) && scheduleData.schedule.length > 0) {
    return normalizeScheduleEntries(scheduleData.schedule);
  }

  if (Array.isArray(scheduleData?.scheduleEntries) && scheduleData.scheduleEntries.length > 0) {
    return normalizeScheduleEntries(scheduleData.scheduleEntries);
  }

  // DESTINATION responses expose the schedule under parks[0].schedule; fall back when top-level is empty
  if (Array.isArray(scheduleData?.parks) && Array.isArray(scheduleData.parks[0]?.schedule)) {
    return normalizeScheduleEntries(scheduleData.parks[0].schedule);
  }

  if (Array.isArray(scheduleData?.schedule)) {
    return normalizeScheduleEntries(scheduleData.schedule);
  }

  if (Array.isArray(scheduleData?.scheduleEntries)) {
    return normalizeScheduleEntries(scheduleData.scheduleEntries);
  }

  return [];
};

export const getOpeningHoursFromSchedule = (scheduleArray, referenceDate = new Date().toISOString().split('T')[0]) => {
  if (!Array.isArray(scheduleArray) || scheduleArray.length === 0) {
    return null;
  }

  const isOperatingDay = (item) => item?.date && (!item.type || item.type === 'OPERATING' || item.type === 'PARK_OPEN');

  const todaySchedule = scheduleArray.find(
    (item) => item.date === referenceDate && isOperatingDay(item)
  );

  if (todaySchedule) {
    return {
      openingTime: todaySchedule.openingTime,
      closingTime: todaySchedule.closingTime,
      date: todaySchedule.date,
      isFuture: false
    };
  }

  const nextSchedule = scheduleArray
    .filter((item) => item.date > referenceDate && isOperatingDay(item))
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  if (!nextSchedule) {
    return null;
  }

  return {
    openingTime: nextSchedule.openingTime,
    closingTime: nextSchedule.closingTime,
    date: nextSchedule.date,
    isFuture: true
  };
};

/**
 * Builds the opening-hours state expected by park pages from any supported schedule payload.
 * The caller must pass a park-local reference date to avoid UTC day-boundary mistakes.
 */
export const buildOpeningHoursStateFromSchedule = (scheduleData, referenceDate = new Date().toISOString().split('T')[0]) => {
  const scheduleEntries = extractScheduleArray(scheduleData);
  const openingHours = getOpeningHoursFromSchedule(scheduleEntries, referenceDate);
  const extraHoursDate = openingHours?.date;
  const extraHours = extraHoursDate
    ? scheduleEntries.find((item) => item.date === extraHoursDate && item.type === 'EXTRA_HOURS')
    : null;

  return {
    ...(openingHours || {
      openingTime: null,
      closingTime: null,
      date: null,
      isFuture: false
    }),
    extraMagicHours: extraHours ? {
      openingTime: extraHours.openingTime,
      closingTime: extraHours.closingTime
    } : null,
    schedule: scheduleEntries,
    scheduleEntries
  };
};

/**
 * Returns true when every day of the zero-based month has a schedule entry.
 * This prevents a single "today" entry from suppressing the monthly calendar fetch.
 */
export const hasScheduleCoverageForMonth = (scheduleData, year, month) => {
  const scheduleEntries = extractScheduleArray(scheduleData);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 0 || month > 11) {
    return false;
  }

  if (scheduleEntries.length === 0) {
    return false;
  }

  const coveredDates = new Set(scheduleEntries.map((entry) => entry?.date).filter(Boolean));
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day += 1) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (!coveredDates.has(dateStr)) {
      return false;
    }
  }

  return true;
};
